import { DateTime } from 'luxon';

import {
    NotificationContent,
    NotificationDto,
    NotificationId,
    NotificationOwner,
    NotificationSourceType,
} from '../models/notification';
import {
    NotificationDetail,
    NotificationGroup,
    NotificationGroups,
    NotificationType,
    NotificationView,
} from '../models/notification-view';

export const toOwnerParams = (owner: NotificationOwner) => ({ firstname: owner.firstName, lastname: owner.lastName });

const MESSAGE_KEYS: Record<NotificationType, { singular?: string; plural?: string }> = {
    linkRequest: { singular: 'NOTIFICATION_ITEM_LINK_REQUEST_ACCEPTED' },
    rejectedLinkRequest: { singular: 'NOTIFICATION_ITEM_LINK_REQUEST_DECLINED' },
    deletedLinkRequest: { singular: 'NOTIFICATION_ITEM_DELETED_LINK_REQUEST' },
    admin: {},
    alertReminder: { singular: 'NOTIFICATION_ITEM_ALERT', plural: 'NOTIFICATION_ITEM_ALERT_PLURAL' },
    linkRequestReminder: { singular: 'NOTIFICATION_ITEM_LINK_REQUEST_REMIND_BODY' },
    interventionExpired: { singular: 'NOTIFICATION_ITEM_INTERVENTION_EXPIRED_BODY' },
};

/** Translation key of a notification's sentence; `admin` has none, its message is verbatim. */
export function notificationMessageKey(type: NotificationType, params: Record<string, unknown>): string | undefined {
    const { singular, plural } = MESSAGE_KEYS[type];
    const count = params['count'];
    return plural !== undefined && typeof count === 'number' && count > 1 ? plural : singular;
}

export function toView(content: NotificationContent): Omit<NotificationView, 'id'> | null {
    switch (content.type) {
        case NotificationSourceType.LINK_REQUEST_ACCEPTED:
            return {
                type: 'linkRequest',
                params: toOwnerParams(content.data.owner),
                gatewayId: content.data.gateways[0],
            };
        case NotificationSourceType.LINK_REQUEST_REJECTED:
            return { type: 'rejectedLinkRequest', params: toOwnerParams(content.data.owner) };
        case NotificationSourceType.LINK_REQUEST_REMINDER:
            return {
                type: 'linkRequestReminder',
                params: toOwnerParams(content.data.owner),
                linkRequestOID: content.data.linkRequestOID,
            };
        case NotificationSourceType.INSTALLER_DETACHED:
            return { type: 'deletedLinkRequest', params: toOwnerParams(content.data.owner) };
        case NotificationSourceType.ALERT_REMINDER:
            return { type: 'alertReminder', params: { count: content.data.alertCount } };
        case NotificationSourceType.INTERVENTION_EXPIRED:
            return {
                type: 'interventionExpired',
                params: toOwnerParams(content.data.owner),
                siteOID: content.data.siteOID,
            };
        case NotificationSourceType.FREE_NOTIFICATION:
            return { type: 'admin', message: content.data.title };
        default:
            return null;
    }
}

function toDateTime(epoch: number): DateTime {
    const milliseconds = Math.abs(epoch) < 1_000_000_000_000 ? epoch * 1000 : epoch;
    return DateTime.fromMillis(milliseconds);
}

interface DecoratedNotification {
    view: NotificationView;
    read: boolean;
    timestamp: number;
    dayKey: string;
}

function decorate(notification: NotificationDto): DecoratedNotification | null {
    const view = toView(notification.notification);
    if (!view) return null;

    const epoch = notification.notification.date ?? notification.date;
    if (epoch === undefined || !Number.isFinite(epoch)) return null;

    const date = toDateTime(epoch);
    const dayKey = date.toISODate();
    if (!date.isValid || !dayKey) return null;

    return {
        view: { id: notification.id, ...view },
        read: Boolean(notification.read),
        timestamp: date.toMillis(),
        dayKey,
    };
}

function groupByDay(items: DecoratedNotification[]): NotificationGroup[] {
    const groups = new Map<string, NotificationGroup>();

    for (const item of items) {
        const existing = groups.get(item.dayKey);
        if (existing) {
            existing.notifications.push(item.view);
        } else {
            groups.set(item.dayKey, { date: item.timestamp, notifications: [item.view] });
        }
    }

    return [...groups.values()];
}

export function toNotificationGroups(notifications: readonly NotificationDto[]): NotificationGroups {
    const decoratedById = notifications
        .map((notification) => decorate(notification))
        .filter((item): item is DecoratedNotification => item !== null)
        .reduce((items, item) => items.set(item.view.id, item), new Map<NotificationId, DecoratedNotification>());
    const decorated = [...decoratedById.values()].sort((a, b) => b.timestamp - a.timestamp);

    return {
        unread: groupByDay(decorated.filter((item) => !item.read)),
        read: groupByDay(decorated.filter((item) => item.read)),
    };
}

export function toNotificationDetail(notification: NotificationDto): NotificationDetail | null {
    const content = notification.notification;
    if (content.type !== NotificationSourceType.FREE_NOTIFICATION) return null;

    return { title: content.data.title, body: content.data.text, read: Boolean(notification.read) };
}
